import { API_URL } from './api';

/**
 * Estado da instalação do PWA (evento `beforeinstallprompt`, iOS, recusa) e
 * telemetria anônima para a Triagem. Módulo sem React para poder ser chamado
 * de qualquer página (ex.: `requestInstallPrompt('ativo_publicado')`).
 */

export type PwaEvent = 'exibido' | 'instalado' | 'recusado' | 'ios_instrucoes' | 'atualizado';
export type PwaPlatform = 'android' | 'ios' | 'desktop' | 'outro';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface InstallState {
  /** Há um `beforeinstallprompt` guardado (Android/Chrome/Edge desktop) */
  canPromptNatively: boolean;
  /** Modal de instalação aberto */
  isOpen: boolean;
  /** Motivo que abriu o modal (telemetria) */
  reason: string | null;
}

const DECLINE_KEY = 'pc-pwa-install-declined-at';
const DECLINE_DAYS = 30;
/** Espera para não aparecer junto com a micro-pesquisa de feedback */
const INTEREST_DELAY_MS = 8000;
const AUTH_ROUTES = ['/login', '/cadastro', '/recuperar-senha', '/completar-cadastro'];

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let state: InstallState = { canPromptNatively: false, isOpen: false, reason: null };
const listeners = new Set<() => void>();
let pendingTimer: ReturnType<typeof setTimeout> | null = null;

function setState(partial: Partial<InstallState>) {
  state = { ...state, ...partial };
  listeners.forEach((listener) => listener());
}

export function subscribeInstallState(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getInstallState(): InstallState {
  return state;
}

export function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  // iPadOS 13+ se apresenta como Mac; toque diferencia
  return /iphone|ipad|ipod/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const nav = navigator as Navigator & { standalone?: boolean };
  return window.matchMedia?.('(display-mode: standalone)').matches === true || nav.standalone === true;
}

export function detectPlatform(): PwaPlatform {
  if (typeof navigator === 'undefined') return 'outro';
  if (isIos()) return 'ios';
  if (/android/i.test(navigator.userAgent)) return 'android';
  if (/windows|macintosh|linux|cros/i.test(navigator.userAgent)) return 'desktop';
  return 'outro';
}

/** Instalável agora: prompt nativo guardado ou iOS fora do modo app */
export function isInstallAvailable(current: InstallState = state): boolean {
  if (isStandalone()) return false;
  return current.canPromptNatively || isIos();
}

function isDeclinedRecently(): boolean {
  try {
    const raw = localStorage.getItem(DECLINE_KEY);
    if (!raw) return false;
    const declinedAt = Number(raw);
    return Number.isFinite(declinedAt) && Date.now() - declinedAt < DECLINE_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

function saveDecline() {
  try {
    localStorage.setItem(DECLINE_KEY, String(Date.now()));
  } catch {
    // Armazenamento indisponível (aba privada): o modal pode reaparecer, sem outro efeito
  }
}

function isAuthRoute(): boolean {
  return AUTH_ROUTES.some((route) => window.location.pathname.startsWith(route));
}

/** Telemetria anônima (sem PII): só evento, plataforma e motivo */
export function trackPwaEvent(evento: PwaEvent, motivo?: string) {
  try {
    void fetch(`${API_URL}/pwa/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ evento, plataforma: detectPlatform(), ...(motivo ? { motivo: motivo.slice(0, 40) } : {}) }),
      keepalive: true,
      credentials: 'omit',
    }).catch(() => undefined);
  } catch {
    // Telemetria nunca pode quebrar a interface
  }
}

/**
 * Sinal de interesse (ex.: publicou ativo, enviou proposta). Abre o modal
 * depois de alguns segundos, se fizer sentido para este aparelho.
 */
export function requestInstallPrompt(reason: string) {
  if (typeof window === 'undefined') return;
  if (pendingTimer) clearTimeout(pendingTimer);
  pendingTimer = setTimeout(() => {
    pendingTimer = null;
    if (state.isOpen || !isInstallAvailable() || isDeclinedRecently() || isAuthRoute()) return;
    openInstallPrompt(reason);
  }, INTEREST_DELAY_MS);
}

/** Abre o modal imediatamente (opção fixa "Instalar aplicativo" do menu) */
export function openInstallPrompt(reason: string) {
  setState({ isOpen: true, reason });
  trackPwaEvent(isIos() && !state.canPromptNatively ? 'ios_instrucoes' : 'exibido', reason);
}

export function closeInstallPrompt({ declined }: { declined: boolean }) {
  if (declined) {
    saveDecline();
    trackPwaEvent('recusado', state.reason ?? undefined);
  }
  setState({ isOpen: false });
}

/** Chama o diálogo nativo; retorna true se o usuário aceitou */
export async function promptNativeInstall(): Promise<boolean> {
  const promptEvent = deferredPrompt;
  if (!promptEvent) return false;
  deferredPrompt = null;
  setState({ canPromptNatively: false });
  await promptEvent.prompt();
  const choice = await promptEvent.userChoice;
  if (choice.outcome === 'dismissed') saveDecline();
  // 'instalado' é enviado pelo evento appinstalled
  if (choice.outcome === 'dismissed') trackPwaEvent('recusado', 'nativo');
  return choice.outcome === 'accepted';
}

let listenersInstalled = false;

/** Registra os eventos globais; chamar uma vez na inicialização */
export function installPwaListeners() {
  if (listenersInstalled || typeof window === 'undefined') return;
  listenersInstalled = true;
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    setState({ canPromptNatively: true });
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    setState({ canPromptNatively: false, isOpen: false });
    trackPwaEvent('instalado', state.reason ?? undefined);
  });
}
